import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { FlashSaleEyebrow } from '~/features/promotions/components/flash-sale-eyebrow';
import {
  type July4848PromoVariant,
  getJuly4848PromoCheckoutHref,
  JULY_48_48_PROMO_CTA_COLOR,
  JULY_48_48_PROMO_CTA_LABEL,
  JULY_48_48_PROMO_GRADIENT,
  JULY_48_48_PROMO_HEADLINE_COLOR,
  JULY_48_48_PROMO_SUBTITLE_COLOR,
  JULY_48_48_PROMO_VARIANTS,
} from '~/features/promotions/constants/july-48-48-promo';

export type July4848PromoBannerProps = {
  variant: July4848PromoVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

/**
 * Catalog 48/48 promo banner (Figma 1672:3999 desktop / 1672:4010 mobile): blue→purple gradient,
 * solid `#175CFF` border, headline + subtitle row, "Save Now" CTA with circled arrow-right icon.
 */
export function July4848PromoBanner({ variant, promoCode, imagePriority, onCtaClick }: July4848PromoBannerProps) {
  const config = JULY_48_48_PROMO_VARIANTS[variant];
  const ariaLabel = `${config.headlineLine1} ${config.headlineLine2}: ${JULY_48_48_PROMO_CTA_LABEL}`;
  const checkoutHref = getJuly4848PromoCheckoutHref(variant, promoCode);

  return (
    <LinkBox
      bgGradient={JULY_48_48_PROMO_GRADIENT}
      border='1px solid'
      borderColor={JULY_48_48_PROMO_CTA_COLOR}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0}>
        <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='eager'
            priority={imagePriority}
            quality={90}
            sizes='100vw'
            src={config.catalogBgMobile}
            style={{ objectFit: 'cover', objectPosition: 'bottom', pointerEvents: 'none' }}
            fill
          />
        </Box>
        <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='eager'
            priority={imagePriority}
            quality={90}
            sizes='656px'
            src={config.catalogBg}
            style={{ objectFit: 'cover', objectPosition: 'bottom', pointerEvents: 'none' }}
            fill
          />
        </Box>
      </Box>
      <Flex
        alignItems={{ base: 'flex-start', md: 'flex-end' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 4 }}
        height='100%'
        justifyContent='space-between'
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '20px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Box maxWidth={{ md: '374px' }} minWidth={0}>
          <Box mb={{ base: 1.5, md: 2 }}>
            <FlashSaleEyebrow size='compact' />
          </Box>
          <Heading
            as='h3'
            color={JULY_48_48_PROMO_HEADLINE_COLOR}
            fontSize={{ base: '1.25rem', md: '1.5rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={1.15}
            whiteSpace='pre-wrap'
          >
            {`${config.headlineLine1} ${config.headlineLine2}`}
          </Heading>
          <Text color={JULY_48_48_PROMO_SUBTITLE_COLOR} fontSize={{ base: '13px', md: 'md' }} lineHeight={1.5}>
            {config.subtitle}
          </Text>
        </Box>
        <Button
          alignItems='center'
          as='span'
          backgroundColor={JULY_48_48_PROMO_CTA_COLOR}
          borderRadius='4px'
          color='white'
          display='inline-flex'
          flexShrink={0}
          fontSize='12px'
          fontWeight='400'
          height='auto'
          leftIcon={<CircleArrowRightIcon boxSize='14px' color='white' aria-hidden />}
          lineHeight={1.2}
          paddingX='14px'
          paddingY='10px'
          pointerEvents='none'
          tabIndex={-1}
          variant='unstyled'
          aria-hidden
        >
          {JULY_48_48_PROMO_CTA_LABEL}
        </Button>
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={checkoutHref}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
